<script setup>
import {computed, ref} from "vue";
import {ApprovalCategoryEnum, useApprovalCenterFilters} from "@/composables/approval-center/useApprovals";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import ReviewFix from "@/components/approval-center/partials/ReviewFix.vue";
import BlogFix from "@/components/approval-center/partials/BlogFix.vue";
import KitIcon from "@/components/approval-center/partials/kit/KitIcon.vue";
import BlogStrategyFix from "@/components/approval-center/partials/BlogStrategyFix.vue";
import FAQFix from "@/components/approval-center/partials/FAQFix.vue";
import NewQuestionFix from "@/components/approval-center/partials/NewQuestionFix.vue";
import ToneOfVoiceFix from "@/components/approval-center/partials/ToneOfVoiceFix.vue";
import WebsiteCopyFix from "@/components/approval-center/partials/WebsiteCopyFix.vue";
import GBPPostFix from "@/components/approval-center/partials/GBPPostFix.vue";
import FilterPills from "@/components/approval-center/partials/FilterPills.vue";
import GBPDescriptionFix from "@/components/approval-center/partials/GBPDescriptionFix.vue";
import ZeroContent from "@/components/global-themed/ZeroContent.vue";
import {useQueryClient} from "@tanstack/vue-query";
import {isOnLocalDomain} from "@/composables/useLocalDomains";
import HeadingFix from "@/components/approval-center/partials/HeadingFix.vue";
import MetaDescriptionFix from "@/components/approval-center/partials/MetaDescriptionFix.vue";
import PageTitleFix from "@/components/approval-center/partials/PageTitleFix.vue";

const {activeFilter, totalCount} = useApprovalCenterFilters();

const approvalComponents = {
  [ApprovalCategoryEnum.BLOG_STRATEGY]: BlogStrategyFix,
  [ApprovalCategoryEnum.BLOG_POST]: BlogFix,
  [ApprovalCategoryEnum.GBP_POST]: GBPPostFix,
  [ApprovalCategoryEnum.TONE_OF_VOICE]: ToneOfVoiceFix,
  [ApprovalCategoryEnum.GBP_DESCRIPTION]: GBPDescriptionFix,
  [ApprovalCategoryEnum.GBP_FAQ]: FAQFix,
  [ApprovalCategoryEnum.GBP_NEW_QUESTIONS]: NewQuestionFix,
  [ApprovalCategoryEnum.GBP_REVIEWS]: ReviewFix,
  [ApprovalCategoryEnum.HEADING]: HeadingFix,
  [ApprovalCategoryEnum.WEBSITE_COPY]: WebsiteCopyFix,
  [ApprovalCategoryEnum.META_DESCRIPTION]: MetaDescriptionFix,
  [ApprovalCategoryEnum.PAGE_TITLE]: PageTitleFix,
};

const visibleComponents = computed(() => {
  if (!activeFilter.value) {
    return approvalComponents;
  }

  return {[activeFilter.value]: approvalComponents[activeFilter.value]};
});

const isLoading = ref(true);
const queryClient = useQueryClient();
let unsubscribe = null;

const checkInitialLoading = () => {
  const initialComplete = queryClient.getQueriesData({queryKey: ["approvals"]})
    .every(([key]) => queryClient.getQueryState(key)?.status === "success")

  if (initialComplete) {
    isLoading.value = false;
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
  }
}

unsubscribe = queryClient.getQueryCache().subscribe(() => {
  // only show loader on initial loading
  checkInitialLoading();
});
</script>

<template>
  <div class="tw-m-auto tw-max-w-default-container">
    <div class="tw-flex tw-flex-col tw-gap-y-8">
      <div>
        <div class="tw-flex tw-items-center tw-justify-between">
          <h1 class="tw-font-bold tw-text-4.5xl tw-text-neutral-900">
            Welcome to the Approval Centre
          </h1>
          <KitIcon class="tw-h-14" />
        </div>

        <p
          v-if="isOnLocalDomain"
          class="tw-text-neutral tw-text-base"
        >
          Review and approve Kit's SEO updates in one place—blog posts, website copy, and more, all ready for your
          approval.
        </p>

        <p
          v-else
          class="tw-text-neutral tw-text-base"
        >
          Review and approve your SEO updates in one place—blog posts, website copy, and more, all ready for your approval.
        </p>
      </div>

      <div
        v-if="isLoading"
        class="tw-bg-white tw-p-8 tw-rounded-lg"
      >
        <LoadingBlock />
      </div>

      <div
        v-show="!isLoading"
        class="tw-bg-white tw-p-8 tw-rounded-lg tw-flex tw-flex-col tw-gap-y-8"
      >
        <FilterPills />

        <ZeroContent v-if="totalCount < 1">
          Looks like there are no approvals to display.
        </ZeroContent>

        <component
          :is="component"
          v-for="(component, category) in visibleComponents"
          :key="category"
          :category="category"
        />
      </div>
    </div>
  </div>
</template>
